<script>
  import { getContext, onMount } from "svelte";
  import { useLocation } from "svelte-navigator";
  import Header from "lib/components/Header.svelte";
  import { Modal, SectionWrapper } from "paxray-components-svelte";
  import { Button } from "paxray-components-svelte";
  import { ToastNotification } from "carbon-components-svelte";
  import AdminFeatures from "./components/admin/AdminFeatures.svelte";
  import AdminOperations from "./components/admin/AdminOperations.svelte";
  import AdminAccess from "./components/admin/AdminAccess.svelte";
  import AdminData from "./components/admin/AdminData.svelte";
  import AdminSetupTest from "./components/admin/AdminSetupTest.svelte";
  import AdminServerLogs from "./components/admin/AdminServerLogs.svelte";
  import DefaultAnonymizationRules from "./components/admin/DefaultAnonymizationRules.svelte";
  import {
    DEFAULT_UI_FEATURES,
    loadUiFeatures,
    saveUiFeatures,
  } from "@stores/uiFeatureStore.js";
  import { saveLlmSettings } from "@stores/llmSettingsStore.js";
  import { DOWNLOAD_ICON, UPLOAD_ICON } from '@utils/icons.js';


  const role = getContext("role");
  const location = useLocation();
  const adminSections = [
    "feature-management",
    "system-operations",
    "access-management",
    "data-management",
    "service-status",
    "platform-setup-test",
    "default-anonymization-rules",
  ];
  const adminSectionLabels = [
    "Feature Management",
    "System Operations",
    "Access Management",
    "Data Management",
    "Service Status",
    "Platform Setup Test (WIP)",
    "Global Anonymization",
  ];
  let features = structuredClone(DEFAULT_UI_FEATURES);
  let loading = true;
  let saving = false;
  let message = "";
  let saveToastMsg = "";
  let saveSuccessful = false;
  let globalAnonDrafts = { windowtitles: [], elements: [] };
  let defaultAnonRules;
  let anonRulesReadonly = false;
  let anonCsvInput;
  let importErrorOpen = false;
  let importErrors = [];
  let importFileName = "";

  $: isAdmin = $role?.role === "admin";
  $: requestedAdminSection = new URLSearchParams($location?.search || "").get(
    "section",
  );
  $: activeAdminSection = Math.max(
    adminSections.indexOf(requestedAdminSection),
    0,
  );

  onMount(async () => {
    features = structuredClone(await loadUiFeatures());
    loading = false;
  });

  async function importAnonCsv(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    saveToastMsg = "";
    importErrorOpen = false;
    importErrors = [];
    importFileName = file.name;
    try {
      const { imported, skipped } = await defaultAnonRules.importCsv(file);
      saveSuccessful = true;
      saveToastMsg = `${imported} rules imported, ${skipped} duplicates skipped.`;
    } catch (error) {
      saveSuccessful = false;
      importErrors = error.details || [error.message || "Unable to read the CSV file."];
      importErrorOpen = true;
    }
  }

  async function save() {
    saving = true;
    saveToastMsg = "";
    try {
      features = structuredClone(await saveUiFeatures(features));
      // Sprachmodell-Einstellungen (General Settings) gehen mit demselben Knopf mit.
      await saveLlmSettings();
      saveSuccessful = true;
      saveToastMsg = "Settings saved.";
    } catch (error) {
      saveSuccessful = false;
      saveToastMsg = error.message;
    } finally {
      saving = false;
    }
  }
</script>

<Header
  section=""
  area={isAdmin ? "Admin Settings" : ""}
  areaHref=""
  areaClickable={false}
  breadcrumb={isAdmin ? adminSectionLabels[activeAdminSection] : "Settings"}
  tooltipText={isAdmin && activeAdminSection === 6
    ? "Manage anonymization rules across projects. The tabs follow the processing order from left to right: allowlists, global regex patterns, default rules, then project rules. Title and UI tabs cover window titles and UI elements respectively."
    : ""}
>
  {#if isAdmin && activeAdminSection === 0}
    <Button
      text={saving ? "Saving..." : "Save settings"}
      kind="secondary"
      size="field"
      disabled={loading || saving}
      on:click={save}
    />
  {:else if isAdmin && activeAdminSection === 6}
    <!-- Button reicht kein title weiter, daher Tooltip am Wrapper. -->
    <span
      class="header-action"
      title={anonRulesReadonly
        ? "Built-in rules are read only"
        : "Imports a CSV into the active tab only"}
    >
      <Button
        text="Import"
        kind="secondary"
        size="small"
        iconLeft={true}
        icon={UPLOAD_ICON}
        disabled={anonRulesReadonly}
        on:click={() => anonCsvInput.click()}
      />
    </span>
    <span class="header-action" title="Exports the rules of the active tab only">
      <Button
        text="Export"
        kind="secondary"
        size="small"
        iconLeft={true}
        icon={DOWNLOAD_ICON}
        on:click={() => defaultAnonRules.exportCsv()}
      />
    </span>
    <input
      bind:this={anonCsvInput}
      type="file"
      accept=".csv"
      hidden
      on:change={importAnonCsv}
    />
  {/if}
</Header>

<SectionWrapper fullscreen seamless={true} seamlessPadding={true}>
  <main slot="main">
    {#if !isAdmin}
      <div class="page-heading">
        <div class="table_heading">
          <h5>My Account</h5>
          <div>Manage your username, email and password.</div>
        </div>
      </div>
    {/if}

    {#if message}
      <div class:error={message !== "Settings saved."} class="message">
        {message}
      </div>
    {/if}

    {#if isAdmin}
      {#if activeAdminSection === 0}
        <AdminFeatures bind:features />
      {:else if activeAdminSection === 1}
        <AdminOperations />
      {:else if activeAdminSection === 2}
        <AdminAccess />
      {:else if activeAdminSection === 3}
        <AdminData />
      {:else if activeAdminSection === 4}
        <AdminServerLogs />
      {:else if activeAdminSection === 5}
        <AdminSetupTest />
      {:else if activeAdminSection === 6}
        <DefaultAnonymizationRules
          bind:this={defaultAnonRules}
          bind:drafts={globalAnonDrafts}
          bind:readonly={anonRulesReadonly}
        />
      {/if}
    {/if}
  </main>
</SectionWrapper>

<Modal
  bind:open={importErrorOpen}
  modalHeading="CSV import failed"
  primaryButtonText="Close"
  hasScrollingContent
  preventCloseOnClickOutside
  on:submit={() => (importErrorOpen = false)}
>
  <div class="import-errors">
    <p><strong>{importFileName}</strong></p>
    <p>No rules were imported. Correct the errors below and import the file again.</p>
    <ul>
      {#each importErrors as error}
        <li>{error}</li>
      {/each}
    </ul>
  </div>
</Modal>

{#if saveToastMsg}
  <div class="toast-wrapper">
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={saveToastMsg}
      caption={new Date().toLocaleString()}
      on:close={(event) => {
        event.preventDefault();
        saveToastMsg = "";
        saveSuccessful = false;
      }}
    />
  </div>
{/if}

<style>
  .import-errors {
    overflow-wrap: anywhere;
  }
  .import-errors p {
    margin: 0 0 1rem;
  }
  .import-errors ul {
    margin: 0;
    padding-left: 1.25rem;
    list-style: disc;
  }
  .import-errors li {
    display: list-item;
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
  .header-action {
    display: inline-flex;
  }
  main {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
  .page-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
  .page-heading .table_heading {
    flex: 1;
  }
  .table_heading {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    background: var(--pax-white);
    border: 1px solid var(--pax-black);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
  }
  .table_heading h5 {
    margin: 0;
    color: var(--pax-black);
  }
  .table_heading div {
    color: #6f7681;
    font-size: 0.85rem;
    line-height: 1.1;
  }
  .toast-wrapper :global(.bx--toast-notification__details) {
    flex: 1 1 0%;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .toast-wrapper {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    width: min(26rem, calc(100vw - 2rem));
    z-index: 9000;
  }
</style>
